// react
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// tanstack
import { useLocation } from '@tanstack/react-router';

// material ui icons
import CheckIcon from '@mui/icons-material/Check';
import CopyAllIcon from '@mui/icons-material/CopyAll';

// material ui
import { IconButton, Tooltip } from '@mui/material';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

const copyWithFallback = async (text) => {
  // Preferred modern API
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Fallback
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

/**
 * Standalone copy-to-clipboard IconButton.
 *
 * Agnostic to what is being copied:
 * - Provide `getText()` or `text`.
 * - Defaults to copying the current URL (origin + pathname + search + hash).
 *
 * Props:
 * - getText?: () => string
 * - text?: string
 * - tooltip?: string
 * - copiedTooltip?: string
 * - successMessage?: string
 * - errorMessage?: string
 * - onCopied?: ({ text: string, source: 'getText' | 'text' | 'url' }) => void
 * - disabled?: boolean
 * - size?: 'small' | 'medium' | 'large'
 * - color?: MUI IconButton color
 * - resetMs?: number
 */
export const CopyToClipboardButton = ({
  getText,
  text,
  tooltip = 'Copy URL to clipboard',
  copiedTooltip = 'Copied',
  successMessage = 'Copied URL to clipboard!',
  errorMessage = 'Could not copy to clipboard',
  onCopied,
  disabled = false,
  size = 'small',
  color = 'default',
  resetMs = 2200,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [copied, setCopied] = useState(false);

  const resolvedText = useMemo(() => {
    if (typeof getText === 'function')
      return { value: getText(), source: 'getText' };
    if (typeof text === 'string') return { value: text, source: 'text' };

    // Default: current URL
    return {
      value: `${window.location.origin}${location.pathname}${location.searchStr}${location.hash}`,
      source: 'url',
    };
  }, [getText, text, location.pathname, location.searchStr, location.hash]);

  const handleCopy = useCallback(async () => {
    try {
      const toCopy = resolvedText.value ?? '';
      if (!toCopy.trim()) {
        dispatch(
          showSnackbar({
            message: errorMessage,
            severity: 'warning',
          }),
        );
        return;
      }

      await copyWithFallback(toCopy);

      setCopied(true);
      dispatch(
        showSnackbar({
          message: successMessage,
          severity: 'success',
        }),
      );

      if (typeof onCopied === 'function') {
        onCopied({ text: toCopy, source: resolvedText.source });
      }

      window.setTimeout(() => setCopied(false), resetMs);
    } catch (err) {
      setCopied(false);
      dispatch(
        showSnackbar({
          message: errorMessage,
          severity: 'error',
        }),
      );
    }
  }, [
    dispatch,
    errorMessage,
    successMessage,
    onCopied,
    resolvedText.value,
    resolvedText.source,
    resetMs,
  ]);

  return (
    <Tooltip title={copied ? copiedTooltip : tooltip}>
      {/* span wrapper keeps Tooltip working when IconButton is disabled */}
      <span>
        <IconButton
          onClick={handleCopy}
          disabled={disabled}
          size={size}
          color={color}
          aria-label={tooltip}
          sx={{ ':hover': { backgroundColor: 'none' } }}
        >
          {copied ? (
            <CheckIcon fontSize="inherit" color="success" />
          ) : (
            <CopyAllIcon fontSize="inherit" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};
